import React from 'react';
import { useAuth } from '@clerk/clerk-react';
import { contractorHelpContent } from '../../content/help/contractorHelpContent';
import { homeownerHelpContent } from '../../content/help/homeownerHelpContent';
import { propertyManagerHelpContent } from '../../content/help/propertyManagerHelpContent';
import { getSupportTemplate } from '../../content/help/helpSupportTemplates';
import type { SupportTemplateKey } from '../../content/help/helpSupportTemplates';
import type { WalkthroughRole } from '../../content/help/walkthroughTypes';
import { buildRoleHelpLink, getRoleDownloadLink } from './helpLinkUtils';
import { sendHelpMessage } from './helpSendAdapter';

type SupportRole = WalkthroughRole | 'property_manager';

interface HelpSupportOpsPanelProps {
  defaultRole?: SupportRole;
  contextLabel?: string;
  recipientName?: string;
}

type LinkType = 'help-home' | 'help-section' | 'download';

export default function HelpSupportOpsPanel({
  defaultRole = 'homeowner',
  contextLabel = 'Support utility',
  recipientName = 'there',
}: HelpSupportOpsPanelProps) {
  const { getToken } = useAuth();
  const [role, setRole] = React.useState<SupportRole>(defaultRole);
  const [linkType, setLinkType] = React.useState<LinkType>('help-home');
  const [sectionId, setSectionId] = React.useState('');
  const [note, setNote] = React.useState('');
  const [copyState, setCopyState] = React.useState<'link' | 'message' | null>(null);
  const [sendState, setSendState] = React.useState<string>('');

  const sections =
    role === 'homeowner'
      ? homeownerHelpContent
      : role === 'property_manager'
      ? propertyManagerHelpContent
      : contractorHelpContent;
  const activeSection = sections.find((s) => s.id === sectionId) || sections[0];

  React.useEffect(() => {
    setSectionId(sections[0]?.id || '');
  }, [role]);

  const resolvedLink = React.useMemo(() => {
    if (linkType === 'help-home') return buildRoleHelpLink(role);
    if (linkType === 'help-section') return buildRoleHelpLink(role, sectionId || activeSection?.id);
    return getRoleDownloadLink(role) || buildRoleHelpLink(role);
  }, [role, linkType, sectionId, activeSection]);

  const templateKey: SupportTemplateKey = (() => {
    if (role === 'homeowner' && linkType === 'help-home') return 'homeowner_general';
    if (role === 'homeowner' && linkType === 'help-section') return 'homeowner_section';
    if (role === 'homeowner') return 'homeowner_download';
    if (role === 'contractor' && linkType === 'help-home') return 'contractor_general';
    if (role === 'contractor' && linkType === 'help-section') return 'contractor_section';
    if (role === 'contractor') return 'contractor_download';
    // property_manager: fall back to homeowner general template
    return 'homeowner_general';
  })();

  const template = getSupportTemplate(templateKey);
  const message = React.useMemo(() => {
    const base = (template?.body || '')
      .replaceAll('{{name}}', recipientName)
      .replaceAll('{{sectionTitle}}', activeSection?.title || 'this section')
      .replaceAll('{{link}}', resolvedLink);
    return note.trim() ? `${base}\n\nSupport note: ${note.trim()}` : base;
  }, [template, recipientName, activeSection, resolvedLink, note]);

  const copy = async (kind: 'link' | 'message') => {
    try {
      await navigator.clipboard.writeText(kind === 'link' ? resolvedLink : message);
      setCopyState(kind);
      window.setTimeout(() => setCopyState(null), 1200);
    } catch {
      setCopyState(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Send Help</h3>
      <p className="text-sm text-gray-600 mb-4">{contextLabel}: generate role-specific help links and support-ready messages.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Audience</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as SupportRole)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="homeowner">Homeowner</option>
            <option value="contractor">Contractor</option>
            <option value="property_manager">Property Manager</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Link type</label>
          <select
            value={linkType}
            onChange={(e) => setLinkType(e.target.value as LinkType)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="help-home">Help Center home</option>
            <option value="help-section">Exact Help section</option>
            <option value="download">Walkthrough download</option>
          </select>
        </div>
      </div>

      {linkType === 'help-section' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Help section</label>
          <select
            value={sectionId}
            onChange={(e) => setSectionId(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          >
            {sections.map((section) => (
              <option key={section.id} value={section.id}>
                {section.title}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Optional support note</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional context for this user"
          className="w-full border border-gray-300 rounded-md px-3 py-2"
        />
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-3">
        <div className="text-xs text-slate-500 mb-1">Resolved link</div>
        <div className="text-sm break-all text-slate-800">{resolvedLink}</div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-4">
        <div className="text-xs text-slate-500 mb-1">Message preview</div>
        <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans m-0">{message}</pre>
      </div>

      <div className="flex flex-wrap gap-2">
        <button className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm" onClick={() => copy('link')}>
          Copy link
        </button>
        <button className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm" onClick={() => copy('message')}>
          Copy message
        </button>
        <button
          className="px-3 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 text-sm"
          onClick={async () => {
            setSendState('Sending…');
            const result = await sendHelpMessage({ role, message, category: linkType }, getToken);
            setSendState(result.ok ? 'Sent.' : `Error: ${result.error ?? 'Unknown error'}`);
            window.setTimeout(() => setSendState(''), 2200);
          }}
        >
          Send help
        </button>
      </div>

      {(copyState || sendState) && (
        <div className="mt-3 text-sm text-green-700">
          {copyState === 'link' && 'Copied link'}
          {copyState === 'message' && 'Copied message'}
          {sendState}
        </div>
      )}
    </div>
  );
}

